import { ReactNode, createContext, useCallback, useContext, useEffect } from 'react'

import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { useGlobalKeydownEvent } from 'src/hooks/useGlobalEvent'
import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { transcriptHttpClient } from 'src/network'
import { analyticsService } from 'src/services/AnalyticsService'
import { getTranscriptJobId, isChromakeyModeActive } from 'src/utils/env'
import { HOTKEYS } from 'src/utils/hotkeys'

import { useConfig } from './ConfigurationContext'
import { useFeatureFlag } from './FeatureFlagContext'
import { useLanguage } from './LanguageContext'
import { useTranscriptClient, useTranscriptUserStateManager } from './Transcript/TranscriptContext'

const EDIT_PERMISSIONS_QUERY_KEY = 'edit-permissions'

type EditingLockToggleSource = 'menu' | 'mobile_menu' | 'hotkey'

type EditorContextValue = {
    isTextEditingAvailable: boolean
    // Effective permissions: what /authorize granted, minus the user's editing lock.
    isTranscriptEditAuthorised: boolean
    isSpeakerEditAuthorised: boolean
    isEditingLockAvailable: boolean
    isEditingLocked: boolean
    toggleEditingLock: (source: EditingLockToggleSource) => void
}

const EditorContext = createContext<EditorContextValue | null>(null)

type EditorProviderProps = {
    children: ReactNode
}

export const EditorProvider = (props: EditorProviderProps) => {
    const { children } = props

    const isTextEditingFeatureEnabled = useFeatureFlag('text_editing')
    const isTextEditingAvailable = isTextEditingFeatureEnabled && !isChromakeyModeActive
    const { requestedLanguageCode } = useLanguage()
    const transcriptClient = useTranscriptClient()
    const userStateManager = useTranscriptUserStateManager()
    const [config, setProp] = useConfig()

    // The previous language's permissions stay while the new ones load, so the lock switch and banner don't
    // flicker on a language change. A failed fetch has no data, which drops the permissions.
    const { data: grantedPermissions } = useQuery({
        queryKey: [EDIT_PERMISSIONS_QUERY_KEY, getTranscriptJobId(), requestedLanguageCode],
        queryFn: () =>
            transcriptHttpClient.getEditingPermissions(getTranscriptJobId(), requestedLanguageCode).catch((error) => {
                console.error('Failed to fetch speaker edit authorization:', error)
                throw error
            }),
        enabled: isTextEditingAvailable,
        placeholderData: keepPreviousData,
        retry: false,
        refetchOnWindowFocus: false,
    })
    const isTranscriptEditGranted = grantedPermissions?.editTranscript ?? false
    const isSpeakerEditGranted = grantedPermissions?.editSpeakers ?? false

    const isEditingLockAvailable = isTextEditingAvailable && (isTranscriptEditGranted || isSpeakerEditGranted)
    const isEditingLocked = isEditingLockAvailable && config.isEditingLocked
    // Cached grants outlive a loss of availability (a disabled query keeps its data), so gate on it here.
    const isTranscriptEditAuthorised = isTextEditingAvailable && isTranscriptEditGranted && !isEditingLocked
    const isSpeakerEditAuthorised = isTextEditingAvailable && isSpeakerEditGranted && !isEditingLocked

    useEffect(() => {
        transcriptClient.setReadOnly(!isTranscriptEditAuthorised)
    }, [isTranscriptEditAuthorised, transcriptClient])

    useEffect(() => {
        if (isEditingLocked && userStateManager.getLocalState().selection) {
            userStateManager.updateSelection(null)
        }
    }, [isEditingLocked, userStateManager])

    const toggleEditingLock = useCallback(
        (source: EditingLockToggleSource) => {
            const isLocked = !config.isEditingLocked
            setProp('isEditingLocked', isLocked)
            analyticsService.track('Lock editing switch toggled', { isEditingLocked: isLocked, source })
        },
        [config.isEditingLocked, setProp],
    )

    const onToggleEditingLockHotkey = useCallback(
        (event: KeyboardEvent) => {
            if (!isEditingLockAvailable || !HOTKEYS.toggleEditingLock.check(event) || event.repeat) {
                return
            }

            event.preventDefault()
            toggleEditingLock('hotkey')
        },
        [isEditingLockAvailable, toggleEditingLock],
    )
    useGlobalKeydownEvent(onToggleEditingLockHotkey)

    return (
        <EditorContext.Provider
            value={useShallowMemo({
                isTextEditingAvailable,
                isSpeakerEditAuthorised,
                isTranscriptEditAuthorised,
                isEditingLockAvailable,
                isEditingLocked,
                toggleEditingLock,
            })}>
            {children}
        </EditorContext.Provider>
    )
}

export const useEditor = () => {
    const contextValue = useContext(EditorContext)

    if (contextValue === null) {
        throw new Error('You have forgot to use <EditorContext.Provider />, shame on you.')
    }

    return contextValue
}
